import React from "react";
import { ChevronLeft, ChevronRight, CalendarCheck2 } from "lucide-react";
import { Button } from "./ui/button";

const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const meseLabel = (m) => { try { return capFirst(new Date(m + "-01T00:00:00").toLocaleDateString("it-IT", { month: "long", year: "numeric" })); } catch { return m; } };
const fmtGiornoLong = (iso) => { try { return capFirst(new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })); } catch { return iso; } };
const last12Months = () => { const a = []; const d = new Date(); d.setDate(1); for (let i = 0; i < 12; i++) { a.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); d.setMonth(d.getMonth() - 1); } return a; };
const DOW_ABBR = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Box recap del GIORNO: card con KPI (riepilogo argomento) + box operatori.
const TONE = {
  ok: { br: "border-emerald-200", bg: "bg-emerald-50", tx: "text-emerald-700" },
  warn: { br: "border-amber-200", bg: "bg-amber-50", tx: "text-amber-700" },
  bad: { br: "border-red-200", bg: "bg-red-50", tx: "text-red-700" },
  muted: { br: "border-slate-200", bg: "bg-slate-50", tx: "text-slate-800" },
};
export function RecapGiorno({ operatori = [], stats = [], color = "#1f7a5c", showOperatori = true, opLabel = "azioni", mb = true }) {
  return (
    <div className={`rounded-2xl border-2 bg-white p-4 shadow-sm ${mb ? "mb-4" : ""}`} style={{ borderColor: color + "40" }} data-testid="recap-giorno">
      <div className="flex items-center gap-2 mb-3">
        <span className="grid place-items-center h-7 w-7 rounded-lg" style={{ background: color + "1f" }}><CalendarCheck2 size={16} style={{ color }} /></span>
        <span className="font-head font-bold text-sm">Recap del giorno</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 mb-3">
        {stats.map((s) => { const t = TONE[s.tone] || TONE.muted; return (
          <div key={s.key} className={`rounded-xl border ${t.br} ${t.bg} p-3`} data-testid={`recap-giorno-stat-${s.key}`}>
            <div className={`text-2xl font-head font-bold tabnum leading-none ${t.tx}`}>{s.value}</div>
            <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide mt-1.5">{s.label}</div>
          </div>
        ); })}
      </div>
      {showOperatori && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Operatori — {opLabel}</div>
          {operatori.length === 0
            ? <span className="text-sm text-muted-foreground">Nessuna azione registrata in questo giorno.</span>
            : <div className="flex flex-wrap gap-2">{operatori.map((o) => (
                <div key={o.nome} className="flex items-center gap-2 rounded-lg border px-2.5 py-1.5 bg-white" style={{ borderColor: color + "40" }} data-testid={`recap-giorno-op-${o.nome}`}>
                  <span className="text-sm font-semibold">{o.nome}</span>
                  <span className="text-white text-xs font-bold rounded-md px-1.5 py-0.5 tabnum" style={{ background: color }}>{o.count}</span>
                </div>
              ))}</div>}
        </div>
      )}
    </div>
  );
}

// Box di ALLERTA ben evidente: titolo + conteggio + elenco item.
export function AllertaBox({ title, icon: Ico, items = [], tone = "bad", emptyText, renderItem, onItemClick, testid = "allerta-box" }) {
  const s = tone === "warn"
    ? { br: "border-amber-300", bg: "bg-amber-50", tx: "text-amber-800", chip: "bg-amber-600" }
    : { br: "border-red-300", bg: "bg-red-50", tx: "text-red-800", chip: "bg-red-600" };
  if (!items.length && emptyText === undefined) return null;
  return (
    <div className={`rounded-2xl border-2 ${s.br} ${s.bg} p-4 mb-4 shadow-sm`} data-testid={testid}>
      <div className={`flex items-center gap-2 mb-2 font-head font-bold ${s.tx}`}>
        {Ico && <Ico size={18} />} {title}
        <span className={`ml-auto text-white text-xs font-bold rounded-full px-2.5 py-0.5 tabnum ${s.chip}`} data-testid={`${testid}-count`}>{items.length}</span>
      </div>
      {items.length === 0
        ? <div className={`text-sm font-semibold ${s.tx}`}>{emptyText}</div>
        : <div className="flex flex-wrap gap-2">{items.map((it, i) => (
            onItemClick
              ? <button key={i} type="button" onClick={() => onItemClick(it)}
                  className="bg-white rounded-lg border px-3 py-1.5 text-sm shadow-sm text-left transition-colors hover:bg-slate-50 hover:border-slate-400 hover:shadow cursor-pointer"
                  data-testid={`${testid}-item`}>{renderItem(it)}</button>
              : <div key={i} className="bg-white rounded-lg border px-3 py-1.5 text-sm shadow-sm" data-testid={`${testid}-item`}>{renderItem(it)}</div>
          ))}</div>}
    </div>
  );
}

// Box recap del mese: card con box-operatori e totale.
export function RecapOperatoriMese({ operatori = [], color = "#1f7a5c", label = "attività", icon: Ico }) {
  const tot = operatori.reduce((s, o) => s + (o.count || 0), 0);
  return (
    <div className="rounded-2xl border-2 bg-white p-4 mb-4 shadow-sm" style={{ borderColor: color + "40" }} data-testid="recap-operatori-mese">
      <div className="flex items-center gap-2 mb-3">
        <span className="grid place-items-center h-7 w-7 rounded-lg" style={{ background: color + "1f" }}>{Ico && <Ico size={16} style={{ color }} />}</span>
        <span className="font-head font-bold text-sm">Recap del mese — {label} per operatore</span>
        <span className="ml-auto flex items-center gap-1.5"><span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Totale</span><span className="text-white text-sm font-bold rounded-md px-2 py-0.5 tabnum" style={{ background: color }}>{tot}</span></span>
      </div>
      {operatori.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessuna attività registrata in questo mese.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
          {operatori.map((o) => (
            <div key={o.nome} className="rounded-xl border p-3 flex items-center justify-between bg-white" style={{ borderColor: color + "40" }} data-testid={`recap-op-${o.nome}`}>
              <span className="text-sm font-semibold truncate mr-2">{o.nome}</span>
              <span className="text-xl font-head font-bold tabnum" style={{ color }}>{o.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Entry mesi/giorni "esplosi" in stile Supervisor Ospiti: striscia mesi -> griglia giorni (con contatore) -> dettaglio giorno.
// Props controllate dal parent: mese, onMese, openDay, onOpenDay, counts {iso:n}, color, countLabel, detailRight, children.
export function MeseGiorniEsplosi({ title, subtitle, Icon, color = "#1f7a5c", mese, onMese, openDay, onOpenDay, counts = {}, countLabel = "attività", detailRight, headerRight, monthTop, detailTop, embedded = false, allowFuture = false, futureMonths = 1, largeCells = false, renderDayExtra, children }) {
  const months = React.useMemo(() => {
    const base = last12Months();
    if (!allowFuture) return base;
    const fut = []; const d = new Date(); d.setDate(1);
    for (let i = 1; i <= futureMonths; i++) { d.setMonth(d.getMonth() + 1); fut.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); }
    return [...fut.reverse(), ...base];
  }, [allowFuture, futureMonths]);
  const todayIso = ymd(new Date());
  const stripRef = React.useRef(null);
  React.useEffect(() => {
    const el = stripRef.current?.querySelector(`[data-testid="mge-mese-${mese}"]`);
    if (el && stripRef.current) { stripRef.current.scrollLeft = el.offsetLeft - stripRef.current.offsetLeft - 8; }
  }, [mese, openDay]);
  const y = +mese.slice(0, 4), m = +mese.slice(5, 7) - 1;
  const ndays = new Date(y, m + 1, 0).getDate();
  const giorni = [];
  for (let d = 1; d <= ndays; d++) {
    const iso = `${mese}-${String(d).padStart(2, "0")}`;
    if (!allowFuture && iso > todayIso) continue;
    giorni.push({ iso, giorno: d, dow: new Date(y, m, d).getDay(), count: counts[iso] || 0 });
  }

  const nextDisabled = !allowFuture && openDay && openDay >= todayIso;
  const shiftDay = (delta) => {
    if (!openDay) return;
    const d = new Date(openDay + "T00:00:00");
    d.setDate(d.getDate() + delta);
    const iso = ymd(d);
    if (!allowFuture && iso > todayIso) return;
    const newMese = iso.slice(0, 7);
    if (newMese !== mese) onMese(newMese);
    onOpenDay(iso);
  };

  return (
    <div className={embedded ? "" : "p-4 sm:p-6 lg:px-10"}>
      {!embedded && (
        <div className="mb-5 flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <h1 className="text-2xl font-head font-bold flex items-center gap-2">{Icon && <Icon size={22} style={{ color }} />} {title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          {headerRight}
        </div>
      )}

      {!openDay ? (
        <>
          <div ref={stripRef} className="flex items-center gap-2 mb-4 overflow-x-auto pb-1" data-testid="mge-month-strip">
            {months.map((mm) => (
              <button key={mm} onClick={() => onMese(mm)} data-testid={`mge-mese-${mm}`}
                className={`px-3.5 py-2 rounded-xl text-sm font-semibold whitespace-nowrap shrink-0 border transition-colors capitalize ${mese === mm ? "text-white border-transparent" : "bg-white hover:bg-secondary"}`}
                style={mese === mm ? { background: color } : {}}>{meseLabel(mm)}</button>
            ))}
          </div>
          {monthTop}
          <h3 className="font-head font-bold text-base mb-3">Giorni di <span className="capitalize">{meseLabel(mese)}</span> <span className="text-xs font-normal text-muted-foreground">— clicca un giorno per aprirlo</span></h3>
          <div className={`grid gap-2.5 ${largeCells ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"}`} data-testid="mge-day-grid">
            {giorni.map((g) => (
              <button key={g.iso} onClick={() => onOpenDay(g.iso)} data-testid={`mge-day-${g.iso}`}
                className={`text-left bg-white rounded-xl border p-3 transition-shadow hover:shadow-md cursor-pointer ${largeCells ? "min-h-[128px]" : ""} ${g.iso === todayIso ? "ring-2" : ""} ${g.count ? "border-current" : ""}`}
                style={g.iso === todayIso ? { boxShadow: `0 0 0 2px ${color}` } : {}}>
                <div className="flex items-baseline justify-between">
                  <div className="font-head font-bold text-lg leading-none">{g.giorno}<span className="text-[10px] font-semibold text-muted-foreground ml-1">{DOW_ABBR[g.dow]}</span></div>
                  {g.count > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: color + "22", color }}>{g.count}</span>}
                </div>
                {renderDayExtra
                  ? renderDayExtra(g.iso)
                  : <div className="text-[11px] text-muted-foreground mt-2">{g.count > 0 ? `${g.count} ${countLabel}` : "—"}{g.iso === todayIso ? " · oggi" : ""}</div>}
              </button>
            ))}
          </div>
        </>
      ) : (
        <div data-testid="mge-detail">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Button variant="outline" size="sm" onClick={() => onOpenDay(null)} data-testid="mge-back"><ChevronLeft size={15} className="mr-1" /> Torna al mese</Button>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shiftDay(-1)} data-testid="mge-prev-day" title="Giorno precedente"><ChevronLeft size={16} /></Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shiftDay(1)} disabled={nextDisabled} data-testid="mge-next-day" title="Giorno successivo"><ChevronRight size={16} /></Button>
            </div>
            <h3 className="font-head font-bold text-base capitalize">{fmtGiornoLong(openDay)}{openDay === todayIso ? " · oggi" : ""}</h3>
            {detailRight && <div className="ml-auto flex items-center gap-2 flex-wrap">{detailRight}</div>}
          </div>
          {detailTop}
          {children}
        </div>
      )}
    </div>
  );
}
